import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Button, Chip, TableCell, TableRow, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import * as React from "react";
import { useEffect, useState } from "react";
import { fetchData } from "../../common/services/ApiClient";
import { sortObjValue, toTitleCase } from "../../common/services/Utilities";
import { ApiBaseUrl } from "../../config";
import SearchPills from "../../containers/SearchPills";
import {
  KeyValue,
  Locations,
  Query,
  SearchPillData,
  Sort,
  StateRow,
  TableHeader,
} from "../../interfaces";
import { selectedStatesAlpha } from "../../refdata/FilterOptions";
import AddressDisplay from "../AddressDisplay";
import ModalDialog from "../ModalDialog";
import MultiHeaderTable from "../MultiHeaderTable/MultiHeaderTable";
import OfficeMapDisplay from "../OfficeMapDisplay";
import ShowHideButton from "../ShowHideButton";

type LocationsApiField = keyof Locations | "address";

interface Props {
  cert: string;
  appState: Query;
  locationsSearch?: string;
}

interface StateListProps {
  open: string[];
  setOpen: React.Dispatch<React.SetStateAction<string[]>>;
  countyData: KeyValue;
  stateData: StateRow[];
  total: number;
}

interface CountyListProps {
  countyData: KeyValue;
  openCounties: string[];
  setOpenCounties: React.Dispatch<React.SetStateAction<string[]>>;
}

const useStyles = makeStyles(theme => ({
  locationPills: {
    "& .MuiCollapse-root": {
      // marginTop: -25,
      marginBottom: 10,
    },
  },
  h4: {
    color: "#1A4480",
    padding: "0",
    fontSize: "1rem",
    fontWeight: "bold",
    lineHeight: "1.5rem",
    paddingBottom: "1rem",
  },
  openCloseAll: {
    "minWidth": 80,
    "margin": 0,
    "color": "#ffffff",
    "fontWeight": 600,
    "backgroundColor": "#426cac",
    "&:hover, &.Mui-focusVisible": {
      color: theme.palette.primary.main,
      backgroundColor: theme.palette.secondary.main,
    },
  },
  tableRowStyles: {
    "& td.MuiTableCell-root.MuiTableCell-body": {
      backgroundColor: "#acc7df",
      fontWeight: "bold",
    },
    "&:hover td.MuiTableCell-root.MuiTableCell-body, &:focus-visible td.MuiTableCell-root.MuiTableCell-body": {
      cursor: "pointer",
      filter: "brightness(.9)",
    },
  },
  totalsRow: {
    "& td.MuiTableCell-root.MuiTableCell-body": {
      backgroundColor: "#194480",
      fontWeight: "bold",
    },
  },
  chip: {
    "cursor": "pointer",
    "backgroundColor": "transparent",
    "color": theme.palette.primary.main,
    "fontSize": 14,
    "float": "left",
    "& .MuiChip-icon": {
      color: theme.palette.primary.main,
    },
  },
  rowIndent: {
    "&.MuiTableCell-root.MuiTableCell-body": {
      textAlign: "left",
    },
  },
  rowIndentCounties: {
    "&.MuiTableCell-root.MuiTableCell-body": {
      textAlign: "left",
      paddingLeft: 30,
    },
  },
  rowIndentInstitutions: {
    "&.MuiTableCell-root.MuiTableCell-body": {
      textAlign: "left",
      paddingLeft: 70,
      color: "blue",
      textDecoration: "underline",
    },
  },
  rowStyles: {
    "& td.MuiTableCell-root.MuiTableCell-body": {
      backgroundColor: theme.palette.secondary.main,
      color: theme.palette.primary.main,
      fontWeight: "bold",
    },
    "&:hover td.MuiTableCell-root.MuiTableCell-body, &:focus-visible td.MuiTableCell-root.MuiTableCell-body": {
      cursor: "pointer",
      filter: "brightness(.93)",
    },
  },
  noResults: {
    "&.MuiTableRow-root td": {
      height: 300,
      paddingTop: "1rem",
      fontSize: "1rem",
      display: "block",
      position: "absolute",
    },
  },
  tableRow: {
    "cursor": "pointer",
    "&.MuiTableRow-root:hover, &.MuiTableRow-root:focus-visible": {
      background: "#BEDBDB",
    },
  },
  mainOfficeRow: {
    "cursor": "pointer",
    "&.MuiTableRow-root:hover, &.MuiTableRow-root:focus-visible": {
      background: "#BEDBDB",
    },
    "& td.MuiTableCell-root.MuiTableCell-body": {
      fontWeight: "bold",
    },
  },
}));

const BankFindLocationsInstituteList = ({ locations }: any) => {
  const classes = useStyles();
  const [openDialog, setOpenDialog] = React.useState(false);

  const [mapData, setMapData] = React.useState<Locations>({
    ACQDATE: "",
    ADDRESS: "",
    ADDRESS2: "",
    CITY: "",
    COUNTY: "",
    ESTYMD: "",
    LATITUDE: 0,
    LONGITUDE: 0,
    MAINOFF: 0,
    NAME: "",
    OFFNAME: "",
    OFFNUM: "",
    SERVTYPE: "",
    SERVTYPE_DESC: "",
    STALP: "",
    UNINUM: "",
    ZIP: "",
  });
  const handleMouseClick = (data: KeyValue, event: KeyValue) => {
    setMapData({
      ACQDATE: data.ACQDATE,
      ADDRESS: data.ADDRESS,
      ADDRESS2: data.ADDRESS2,
      CITY: data.CITY,
      COUNTY: data.COUNTY,
      ESTYMD: data.ESTYMD,
      LATITUDE: data.LATITUDE,
      LONGITUDE: data.LONGITUDE,
      MAINOFF: data.MAINOFF,
      NAME: data.NAME,
      OFFNAME: data.OFFNAME,
      OFFNUM: data.OFFNUM,
      SERVTYPE: data.SERVTYPE,
      SERVTYPE_DESC: data.SERVTYPE_DESC,
      STALP: data.STALP,
      UNINUM: data.UNINUM,
      ZIP: data.ZIP,
    });
    setOpenDialog(true);
  };

  const locationsRows = sortObjValue(locations, "CITY").map(
    (data: KeyValue, index: number) => {
      const mainOffice = data.OFFNUM === 0;
      return (
        <TableRow
          className={mainOffice ? classes.mainOfficeRow : classes.tableRow}
          onClick={event => handleMouseClick(data, event)}
          key={index}
          sx={{ backgroundColor: index % 2 === 1 ? "#E8ECF2" : null }}
        >
          <TableCell className={classes.rowIndentInstitutions}>
            {data.ADDRESS}
          </TableCell>
          <TableCell />
          <TableCell />
          <TableCell>{data.CITY}</TableCell>
          <TableCell>{data.ZIP}</TableCell>
          <TableCell>
            <div id="uninum-value">{data.UNINUM}</div>
          </TableCell>
          <TableCell>{mainOffice ? "Main Office" : data.OFFNUM}</TableCell>
          <TableCell>{data.SERVTYPE_DESC}</TableCell>
          <TableCell>{data.ESTYMD}</TableCell>
          <TableCell>{data.ACQDATE}</TableCell>
        </TableRow>
      );
    },
  );

  return (
    <>
      {locationsRows}
      <ModalDialog
        id="branch-office-modal"
        title={toTitleCase(mapData.OFFNAME)}
        subtitle={`Branch Number: ${
          mapData.MAINOFF === 1 ? "Main Office" : mapData.OFFNUM
        }`}
        display={openDialog}
        setDisplay={setOpenDialog}
      >
        <div style={{ paddingBottom: 16 }}>
          <OfficeMapDisplay data={mapData} useDefaultAddress showDateAcquired />
        </div>
      </ModalDialog>
    </>
  );
};

const BankFindLocationsCountyList = ({
  countyData,
  openCounties,
  setOpenCounties,
}: CountyListProps) => {
  const classes = useStyles();
  let sortedCounties = Object.keys(countyData).map(c => ({
    ...countyData[c],
    name: c,
  }));
  sortedCounties = sortObjValue(sortedCounties, "name");

  const countyList = countyData ? (
    sortedCounties.map(data => {
      const countyName = data.name;
      const countyTotal = data.count;
      const institutions = data.locations;

      const expandCollapse = (e: any) => {
        const otherClicks = ["Open All", "Close All"];
        if (!otherClicks.includes(e.target.innerText)) {
          !openCounties.includes(countyName)
            ? setOpenCounties([...openCounties, countyName])
            : setOpenCounties(
                openCounties.filter(county => county !== countyName),
              );
        }
      };
      const expandCollapseProps = {
        onKeyDown: (e: any) => {
          if (e.key === "Enter") {
            expandCollapse(e);
          }
        },
        onClick: (e: any) => expandCollapse(e),
      };
      return (
        <React.Fragment key={`${countyName}_${countyTotal}`}>
          <TableRow
            id={`${countyName
              .toLowerCase()
              .replace(/ /g, "")}_expand_${countyTotal}`}
            className={classes.rowStyles}
            tabIndex={0}
            {...expandCollapseProps}
          >
            <TableCell className={classes.rowIndentCounties}>
              {openCounties.includes(countyName) ? (
                <Chip
                  className={classes.chip}
                  icon={<ExpandMoreIcon />}
                  label={countyName}
                />
              ) : (
                <Chip
                  className={classes.chip}
                  icon={<ChevronRightIcon />}
                  label={countyName}
                />
              )}
            </TableCell>
            <TableCell></TableCell>
            <TableCell>{countyTotal?.toLocaleString()}</TableCell>
            <TableCell></TableCell>
            <TableCell></TableCell>
            <TableCell></TableCell>
            <TableCell></TableCell>
            <TableCell></TableCell>
            <TableCell></TableCell>
            <TableCell></TableCell>
          </TableRow>
          {openCounties.includes(countyName) && (
            <BankFindLocationsInstituteList locations={institutions} />
          )}
        </React.Fragment>
      );
    })
  ) : (
    <></>
  );

  return <>{countyList}</>;
};

const BankFindLocationsStateList = ({
  open,
  setOpen,
  countyData,
  stateData,
  total,
}: StateListProps) => {
  const classes = useStyles();

  const [openCounties, setOpenCounties] = React.useState<string[]>([]);

  const stateRows = stateData.map((data, int) => {
    const expandCollapse = (e: any) => {
      const otherClicks = ["Open All", "Close All"];
      if (!otherClicks.includes(e.target.innerText)) {
        !open.includes(data.STALP)
          ? setOpen([...open, data.STALP])
          : setOpen(open.filter((state: any) => state !== data.STALP));
      }
    };

    const expandCollapseProps = {
      onKeyDown: (e: any) => {
        if (e.key === "Enter") {
          expandCollapse(e);
        }
      },
      onClick: (e: any) => expandCollapse(e),
    };
    const allCounties = Object.keys(countyData[data.STALP]);

    const handleOpenCloseAll = () => {
      if (allCounties.length === openCounties.length) {
        setOpenCounties([]);
      } else {
        setOpenCounties(allCounties);
      }
    };
    return (
      data?.STALP && (
        <React.Fragment key={`${data.STALP}_${int}`}>
          <TableRow
            id={`${data.STALP.toLowerCase().replace(/ /g, "")}_expand_${int}`}
            className={classes.tableRowStyles}
            tabIndex={0}
            {...expandCollapseProps}
          >
            <TableCell className={classes.rowIndent}>
              {open.includes(data.STALP) ? (
                <Chip
                  className={classes.chip}
                  icon={<ExpandMoreIcon />}
                  label={`${selectedStatesAlpha.find(
                    (state: KeyValue) => state.value === data.STALP,
                  )?.label ?? ""}`}
                />
              ) : (
                <Chip
                  className={classes.chip}
                  icon={<ChevronRightIcon />}
                  label={`${selectedStatesAlpha.find(
                    (state: KeyValue) => state.value === data.STALP,
                  )?.label ?? ""}`}
                />
              )}
            </TableCell>
            <TableCell style={{ padding: 0 }}>
              {open.includes(data.STALP) && (
                <Button
                  id={
                    !open.includes(data.STALP)
                      ? `closeAllCounties_${data.STALP}`
                      : `openAllCounties_${data.STALP}`
                  }
                  className={classes.openCloseAll}
                  onClick={handleOpenCloseAll}
                >
                  {allCounties.length === openCounties.length
                    ? "Close All"
                    : "Open All"}
                </Button>
              )}
            </TableCell>
            <TableCell>{data.count?.toLocaleString()}</TableCell>
            <TableCell></TableCell>
            <TableCell></TableCell>
            <TableCell></TableCell>
            <TableCell></TableCell>
            <TableCell></TableCell>
            <TableCell></TableCell>
            <TableCell></TableCell>
          </TableRow>
          {open.includes(data.STALP) && (
            <BankFindLocationsCountyList
              countyData={countyData[data.STALP]}
              openCounties={openCounties}
              setOpenCounties={setOpenCounties}
            />
          )}
        </React.Fragment>
      )
    );
  });
  const totalsRow = (
    <TableRow className={classes.totalsRow} tabIndex={0}>
      <TableCell className={classes.rowIndent}>
        <Typography color="#FFFFFF" fontWeight="bold">
          Total Offices
        </Typography>
      </TableCell>
      <TableCell></TableCell>
      <TableCell>
        <Typography color="#FFFFFF" fontWeight="bold">
          {total?.toLocaleString()}
        </Typography>
      </TableCell>
      <TableCell></TableCell>
      <TableCell></TableCell>
      <TableCell></TableCell>
      <TableCell></TableCell>
      <TableCell></TableCell>
      <TableCell></TableCell>
      <TableCell></TableCell>
    </TableRow>
  );
  return (
    <>
      {stateRows.length > 0 ? (
        <>
          {stateRows}
          {totalsRow}
        </>
      ) : (
        <TableRow
          className={classes.noResults}
          style={{ backgroundColor: "#fff" }}
        >
          <TableCell>
            "Double-check your search criteria for errors, or try your search
            again limiting or removing location-based criteria."
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

export function createAddressObjectFromLocations(location: Locations) {
  return {
    address: location.ADDRESS,
    address2: location.ADDRESS2,
    city: location.CITY,
    state: location.STALP,
    zip: location.ZIP,
  };
}

export function formattedValues(apiField: LocationsApiField, data: Locations) {
  switch (apiField) {
    case "address":
      return (
        <AddressDisplay
          wrapAddress
          bold={data.MAINOFF === 1}
          addressObject={createAddressObjectFromLocations(data)}
        />
      );
    case "SERVTYPE_DESC":
    case "OFFNAME":
      return toTitleCase(data[apiField]);
    case "OFFNUM":
      return data.MAINOFF === 1 ? "Main Office" : data[apiField];
    default:
      return data[apiField];
  }
}

export default function BankFindLocations({
  cert,
  locationsSearch,
  appState,
}: Props) {
  const classes = useStyles();
  const [locationsData, setLocationsData] = useState<Locations[]>([]);
  // const [matchingOffices, setMatchingOffices] = useState<Metadata>({
  //   count: 0,
  // });
  const [allOffices, setAllOffices] = useState<number>(0);
  const [sort, setSort] = useState<Sort>({
    sortField: "OFFNUM",
    sortOrder: "ASC",
  });
  const [statesOpen, setStatesOpen] = useState<string[]>([]);
  const [stateRows, setStateRows] = useState<any[]>([]);
  const [countyRows, setCountyRows] = useState<KeyValue>({});
  // const [tableOrMap, setTableOrMap] = useState<string>("table");
  const [searchPillData, setSearchPillData] = useState<SearchPillData[]>([]);
  const [searchPillTitle, setSearchPillTitle] = useState<string>("");
  // const [mapData, setMapData] = useState<KeyValue[]>([]);

  const allStates = stateRows
    .filter(curr => curr.STALP !== "")
    .map(curr => curr.STALP);

  const handleOpenCloseAll = () => {
    if (allStates.length === statesOpen.length) {
      setStatesOpen([]);
    } else {
      setStatesOpen(allStates);
    }
  };

  const openCloseButton = (
    <Button
      id={
        allStates.length === statesOpen.length
          ? "closeAllStates"
          : "openAllStates"
      }
      className={classes.openCloseAll}
      onClick={() => {
        handleOpenCloseAll();
      }}
    >
      {allStates.length === statesOpen.length ? "Close All" : "Open All"}
    </Button>
  );
  const HeadersMetaData: KeyValue[] = [
    { title: "State/County/Street Address", apiField: "STALP" },
    {
      title: openCloseButton,
      apiField: "",
    },
    { title: "Matching Offices", apiField: "count" },
    { title: "City (USPS)", apiField: "" },
    { title: "ZIP", apiField: "" },
    { title: "UNINUM", apiField: "" },
    { title: "Office Number", apiField: "" },
    { title: "Service Type", apiField: "" },
    { title: "Established", apiField: "" },
    { title: "Acquired", apiField: "" },
  ];

  const LocationHeaders: TableHeader[][] = [
    HeadersMetaData.map((value: KeyValue) => ({
      title: value.title,
      styles: { width: "full-width" },
      colSpan: 1,
      apiField: value.apiField !== "address" ? value.apiField : undefined,
    })),
  ];

  const locationsView = (
    <MultiHeaderTable
      tableBody={
        <BankFindLocationsStateList
          open={statesOpen}
          setOpen={setStatesOpen}
          countyData={countyRows}
          stateData={stateRows}
          total={locationsData.length}
        />
      }
      tableHeaders={LocationHeaders}
      sortProperties={{
        sortDirection: sort.sortOrder,
        sortField: sort.sortField,
        sortHandler,
      }}
    />
  );
  const header =
    searchPillData.length > 0 ? (
      <ShowHideButton
        title={searchPillTitle}
        id="office-locations-count"
        body={<SearchPills dataArray={searchPillData} />}
      />
    ) : (
      <Typography
        color="primary"
        variant="h6"
        marginBottom={1}
        marginTop={0}
        id="office-locations-count"
      >
        {searchPillTitle}
      </Typography>
    );

  useEffect(() => {
    if (stateRows.length > 0) {
      const stateName = stateRows[0].STNAME;
      const stateCount = locationsData.length;
      const stateHeader = stateRows.length === 1 ? stateName : "";
      const title =
        allOffices !== stateCount
          ? `${stateCount?.toLocaleString()} ${stateHeader} Branch Offices - ${allOffices?.toLocaleString()} total`
          : `${stateCount?.toLocaleString()} Branch Offices`;
      const statePill = appState.filters.stalp !== "" && {
        value: stateName,
        formKey: "stalp",
      };
      const cityPill = appState.search.city !== "" && {
        value: appState.search.city,
        formKey: "city",
        search: true,
      };
      const zipPill = appState.filters.zip !== "" && {
        value: appState.filters.zip,
        formKey: "zip",
      };
      let dataArray: SearchPillData[] = [];
      statePill && dataArray.push(statePill);
      cityPill && dataArray.push(cityPill);
      zipPill && dataArray.push(zipPill);
      setSearchPillTitle(title);
      setSearchPillData(dataArray);
    }
  }, [
    stateRows,
    allOffices,
    appState.filters.stalp,
    appState.filters.zip,
    appState.search.city,
    locationsData.length,
  ]);

  useEffect(() => {
    if (locationsData.length > 0) {
      let tempStateData = Object.values(
        locationsData.reduce((acc: any, bank: any) => {
          acc[bank.STALP] = acc[bank.STALP] || [];
          acc[bank.STALP].push(bank);
          return acc;
        }, {}),
      );
      const stateRow: StateRow[] = tempStateData.map((state: any) => ({
        STALP: state[0].STALP,
        STNAME: state[0].STNAME,
        count: state.length,
      }));
      setStateRows(sortObjValue(stateRow, "STNAME"));

      let tempCountys: any = locationsData.reduce((acc: any, location) => {
        const { COUNTY, STALP } = location;
        if (acc[STALP]) {
          let tempState = { ...acc[STALP] };
          if (Object.hasOwn(tempState, COUNTY)) {
            tempState[COUNTY].count += 1;
            tempState[COUNTY].locations = [
              ...tempState[COUNTY].locations,
              location,
            ];
          } else {
            tempState[COUNTY] = { count: 1, locations: [location] };
          }
          acc[STALP] = tempState;
        } else {
          acc[STALP] = {};
          acc[STALP][COUNTY] = { count: 1, locations: [location] };
        }
        return acc;
      }, {});
      setCountyRows(tempCountys);
    }
  }, [
    appState.filters.stalp,
    appState.search.city,
    appState.filters.zip,
    locationsData,
    allOffices,
  ]);

  useEffect(() => {
    let isMounted = true;
    (async () => {
      await fetchData(
        `${ApiBaseUrl()}/locations?filters=CERT:${cert}&agg_by=CERT`,
      )
        .then(response => {
          if (isMounted) {
            setAllOffices(response.totals.count);
          }
        })
        .catch(error => {
          console.error(`${error.name} : ${error.message}`);
        });
    })();
    return () => {
      isMounted = false;
    };
  }, [cert]);

  useEffect(() => {
    let isMounted = true;
    (async () => {
      await fetchData(
        `${ApiBaseUrl()}/locations?filters=CERT:${cert}${locationsSearch}&limit=10000`,
      )
        .then(response => {
          if (isMounted) {
            // setMatchingOffices(response.totals);
            setLocationsData(response.data);
          }
        })
        .catch(error => {
          console.error(`${error.name} : ${error.message}`);
        });
    })();
    return () => {
      isMounted = false;
    };
  }, [cert, locationsSearch]);

  function sortHandler(tableHeader: TableHeader) {
    if (tableHeader.apiField) {
      const sortDirection = sort.sortOrder === "ASC" ? "DESC" : "ASC";
      setSort({ sortField: tableHeader.apiField, sortOrder: sortDirection });
      if (tableHeader.apiField === "count") {
        const sorted =
          sortDirection === "ASC"
            ? [...stateRows].sort((a, b) => a.count - b.count)
            : [...stateRows].sort((a, b) => b.count - a.count);
        setStateRows(sorted);
      }
      if (tableHeader.apiField === "STALP") {
        const sorted =
          sortDirection === "ASC"
            ? [...stateRows].sort((a, b) => a.STALP.localeCompare(b.STALP))
            : [...stateRows].sort((a, b) => b.STALP.localeCompare(a.STALP));
        setStateRows(sorted);
      }
    }
  }

  // const title = (
  //   <Typography
  //     className={classes.h4}
  //     id="office-locations-count"
  //     marginTop={2}
  //   >
  //     {matchingOffices.count.toLocaleString()}{" "}
  //     {matchingOffices.count === 1 ? "Branch Office" : "Branch Offices"}
  //     {branchOfficeCheck
  //       ? matchingOffices.count === locationsData.length
  //         ? ""
  //         : ` of ${locationsData.length.toLocaleString()}`
  //       : ""}
  //   </Typography>
  // );

  // let mapData = [];
  // let transformedData: any;
  // if (appState.filters.stalp && appState.filters.stalp !== "" && countyData) {
  //   transformedData = countyData[appState.filters.stalp];
  //   transformedData = Object.keys(transformedData).map(county => ({
  //     id: county,
  //     count: transformedData[county].count,
  //   }));
  // } else {
  //   transformedData = stateData.map((state: StateRow) => ({
  //     id: state.STALP,
  //     value: state.count,
  //   }));
  // }
  // mapData = transformedData;

  // const locationsView =
  //   tableOrMap === "table" ? (
  //     <MultiHeaderTable
  //       tableBody={
  //         <BankFindLocationsStateList
  //           open={statesOpen}
  //           setOpen={setStatesOpen}
  //           countyData={countyData}
  //           stateData={stateData}
  //           total={locationsData.length}
  //         />
  //       }
  //       tableHeaders={LocationHeaders}
  //       sortProperties={{
  //         sortDirection: sort.sortOrder,
  //         sortField: sort.sortField,
  //         sortHandler,
  //       }}
  //     />
  //   ) : (
  // <BankFindLocationMap
  //   appState={appState}
  //   stateData={mapData}
  //   updateFilter={updateFilter}
  // />
  // );

  // const tableMapSwitch = (
  //   <Grid
  //     container
  //     sx={{
  //       backgroundColor: "#D9E7F6",
  //       width: 250,
  //       paddingX: 2,
  //       paddingY: 1,
  //       marginBottom: 2,
  //       alignItems: "center",
  //       border: "1px solid #ABC8DF",
  //       borderRadius: 3,
  //     }}
  //   >
  //     <Grid item xs={4}>
  //       <Typography
  //         variant="subtitle1"
  //         fontWeight="bold"
  //         color="primary"
  //         lineHeight={1}
  //       >
  //         Results View:
  //       </Typography>
  //     </Grid>

  //     <Grid item xs={4}>
  //       <Button
  //         disabled={tableOrMap === "table"}
  //         onClick={() => setTableOrMap("table")}
  //         endIcon={<TableChartIcon />}
  //         sx={{
  //           "&.Mui-disabled": {
  //             backgroundColor: "#4B76B7",
  //             color: "white",
  //           },
  //         }}
  //       >
  //         Table
  //       </Button>
  //     </Grid>

  //     <Grid item xs={4} paddingLeft={1}>
  //       <Button
  //         disabled={tableOrMap === "map"}
  //         onClick={() => setTableOrMap("map")}
  //         endIcon={<PlaceIcon />}
  //         sx={{
  //           "&.Mui-disabled": {
  //             backgroundColor: "#4B76B7",
  //             color: "white",
  //           },
  //         }}
  //       >
  //         Map
  //       </Button>
  //     </Grid>
  //   </Grid>
  // );

  return (
    <div id="bankfindLocations" className={classes.locationPills}>
      {header}
      {/* {tableMapSwitch} */}
      {locationsView}
    </div>
  );
}
